import { ReactNode, useEffect, useState } from 'react'
import { TbInfoSquareRounded } from 'react-icons/tb'

import { Breadcrumb } from 'components/Breadcrumbs'
import DocLink from 'components/Docs/DocLink'

export interface SmartBreadcrumbType {
  isMiddle?: boolean
  to?: string
  component: ReactNode
  rightSlot?: ReactNode
}

export function SmartBreadcrumbs({
  breadcrumbs,
  docLink,
}: {
  breadcrumbs: SmartBreadcrumbType[]
  docLink?: string
}) {
  const shortenedBreadcrumbs = useShortenedBreadcrumbs(breadcrumbs)

  const [hovered, setHovered] = useState(false)

  const smartBreadcrumbs = hovered ? breadcrumbs : shortenedBreadcrumbs

  function expand() {
    setHovered(true)
  }

  function collapse() {
    setHovered(false)
  }

  useEffect(() => {
    return collapse()
  }, [])

  function handleMiddleElementMouseEnter() {
    expand()
  }

  return (
    <>
      <div onMouseLeave={collapse}>
        <Breadcrumb.Root>
          {smartBreadcrumbs.map((breadcrumb, index) => (
            <SmartBreadcrumb
              key={index}
              breadcrumb={breadcrumb}
              isLast={index === smartBreadcrumbs.length - 1}
              isFirst={index === 0}
              onMiddleElementMouseEnter={handleMiddleElementMouseEnter}
            />
          ))}
        </Breadcrumb.Root>
      </div>
      {docLink && (
        <DocLink
          leftIcon={<TbInfoSquareRounded />}
          underlined
          marginInlineStart='2'
          path={docLink}
        >
          Docs
        </DocLink>
      )}
    </>
  )
}

function SmartBreadcrumb({
  breadcrumb,
  onMiddleElementMouseEnter,
  isLast,
  isFirst,
}: {
  breadcrumb: SmartBreadcrumbType
  onMiddleElementMouseEnter: () => void
  isLast: boolean
  isFirst: boolean
}) {
  if (breadcrumb.isMiddle) {
    return (
      <Breadcrumb.Text onMouseEnter={onMiddleElementMouseEnter}>
        {breadcrumb.component}
      </Breadcrumb.Text>
    )
  }

  if (!breadcrumb.to) {
    if (isFirst && isLast) {
      return (
        <Breadcrumb.Text
          color='fg.default'
          fontWeight='medium'
          rightSlot={breadcrumb.rightSlot}
        >
          {breadcrumb.component}
        </Breadcrumb.Text>
      )
    }

    return (
      <Breadcrumb.Text rightSlot={breadcrumb.rightSlot}>
        {breadcrumb.component}
      </Breadcrumb.Text>
    )
  }

  return (
    <Breadcrumb.Link to={breadcrumb.to} rightSlot={breadcrumb.rightSlot}>
      {breadcrumb.component}
    </Breadcrumb.Link>
  )
}

function useShortenedBreadcrumbs(breadcrumbs: SmartBreadcrumbType[]) {
  if (breadcrumbs.length < 5) {
    return breadcrumbs
  }

  const firstTwo = breadcrumbs.slice(0, 2)
  const lastTwo = breadcrumbs.slice(breadcrumbs.length - 2, breadcrumbs.length)

  const middle: SmartBreadcrumbType = {
    to: '',
    component: '...',
    isMiddle: true,
  }

  return [...firstTwo, middle, ...lastTwo]
}
